import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal, Form, Input, Select, Radio, Button, Space, Tag, message, Tooltip, Alert, AutoComplete,
} from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';
import { executeDashboardSQL } from '../services/dashboardService';
import { useDashboard } from '../contexts/DashboardContext';

// ── Control types ──────────────────────────────────────────────────
// The user picks a CONTROL TYPE first (input / selector / multi-selector
// / date / date range / switch). The control type is the user-facing
// concept and constrains which operators and value sources are valid.
// Keep this in sync with `_VALID_FILTER_CONTROL_TYPES` in
// `services/manager-service/routers/dashboard.py`.
const CONTROL_TYPES = [
  { value: 'input',          label: 'Input (free text)',     icon: '⌨️' },
  { value: 'selector',       label: 'Selector (single)',     icon: '▾' },
  { value: 'multi_selector', label: 'Multi-selector',        icon: '☰' },
  { value: 'date',           label: 'Date picker',           icon: '📅' },
  { value: 'date_range',     label: 'Date range picker',     icon: '📆' },
  { value: 'switch',         label: 'Switch (on / off)',     icon: '⏼' },
];

// Operators available for each control type. The dialog hides the
// operator picker entirely when the list is a singleton.
const CONTROL_TYPE_OPERATORS = {
  input:          ['=', '!=', 'LIKE', 'ILIKE', 'NOT LIKE', 'NOT ILIKE', '>', '>=', '<', '<='],
  selector:       ['=', '!='],
  multi_selector: ['IN', 'NOT IN'],
  date:           ['=', '!=', '>', '>=', '<', '<='],
  date_range:     ['BETWEEN'],
  switch:         ['IS NULL', 'IS NOT NULL'],
};

// Default operator each control type starts with when the user picks it.
const CONTROL_TYPE_DEFAULT_OP = {
  input: '=',
  selector: '=',
  multi_selector: 'IN',
  date: '=',
  date_range: 'BETWEEN',
  switch: 'IS NULL',
};

// Control types whose values come from a fixed-list / SQL value source.
// For the others the user types values at runtime (input, date, date_range)
// or no value is needed at all (switch).
const VALUE_SOURCE_CONTROL_TYPES = new Set(['selector', 'multi_selector']);

const MULTI_OPS = new Set(['IN', 'NOT IN']);
const PATTERN_OPS = new Set(['LIKE', 'ILIKE', 'NOT LIKE', 'NOT ILIKE']);
const NULL_OPS = new Set(['IS NULL', 'IS NOT NULL']);

/**
 * Derive a control_type for legacy filter definitions saved before
 * control_type existed. Keeps the editor backwards-compatible.
 */
function deriveControlType(op, multiSelect) {
  const u = (op || '').toUpperCase();
  if (NULL_OPS.has(u)) return 'switch';
  if (u === 'BETWEEN') return 'date_range';
  if (MULTI_OPS.has(u)) return multiSelect !== false ? 'multi_selector' : 'selector';
  if (PATTERN_OPS.has(u)) return 'input';
  // Comparison ops default to "input" — the user can switch to
  // selector / date in the editor if appropriate.
  return 'input';
}

/**
 * Custom dashboard-level filter editor.
 *
 * Filters defined here are persisted on the server and wrap every
 * dashboard card's SQL with an additional WHERE clause. Cards whose
 * result-set lacks the configured column are auto-skipped.
 *
 * Props:
 *   open        — modal visibility
 *   initial     — existing filter definition for edit mode, null for create
 *   onCancel()  — close without saving
 *   onSubmit(def) — async submit handler; receives the filter definition
 *                   minus `id` (id is assigned server-side on create or
 *                   preserved on update)
 */
export default function CustomFilterEditorModal({ open, initial, onCancel, onSubmit }) {
  const [form] = Form.useForm();
  const [valueSourceType, setValueSourceType] = useState('fixed');
  const [operator, setOperator] = useState('=');
  const [controlType, setControlType] = useState('selector');
  const [submitting, setSubmitting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewValues, setPreviewValues] = useState([]);
  const isEdit = !!initial;
  // Surface the in-flight draft to the agent so it can suggest/apply
  // edits via the create/edit_dashboard_filter tools while the dialog
  // is open. The signal is cleared on close (and on unmount).
  const { openFilterEditor, updateFilterEditorDraft, closeFilterEditor, items, itemColumnData } = useDashboard();

  // Build the column suggestion list from the currently-open dashboard:
  // union of every component's result-set columns. The Target column
  // input is a free-text AutoComplete so the user can also type a
  // column that isn't yet present in any visible card.
  const columnOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    const safeItems = Array.isArray(items) ? items : [];
    for (const it of safeItems) {
      const colData = itemColumnData?.[it.id];
      const cols = colData?.columns || [];
      for (const c of cols) {
        const key = String(c);
        const norm = key.toLowerCase();
        if (seen.has(norm)) continue;
        seen.add(norm);
        out.push({ value: key, label: key });
      }
    }
    return out.sort((a, b) => a.value.localeCompare(b.value));
  }, [items, itemColumnData]);

  // Build a normalized snapshot of the current form values that the
  // agent can consume directly (matches the persisted-filter shape).
  const snapshotDraft = useCallback((vals) => {
    const ct = vals.control_type || 'selector';
    const op = (vals.operator || CONTROL_TYPE_DEFAULT_OP[ct] || '=').toUpperCase();
    const needsSource = VALUE_SOURCE_CONTROL_TYPES.has(ct);
    return {
      id: initial?.id || null,
      label: (vals.label || '').trim(),
      column: (vals.column || '').trim(),
      operator: op,
      control_type: ct,
      multi_select: ct === 'multi_selector',
      default_values: vals.default_values || [],
      value_source: !needsSource
        ? { type: 'fixed', values: [] }
        : (vals.value_source_type === 'fixed'
            ? { type: 'fixed', values: vals.fixed_values || [] }
            : { type: 'sql', sql: (vals.sql || '').trim() }),
    };
  }, [initial]);

  // Reset form whenever the modal is opened
  useEffect(() => {
    if (!open) return;
    const init = initial || {};
    const vs = init.value_source || { type: 'fixed', values: [] };
    const ct = init.control_type || deriveControlType(init.operator, init.multi_select);
    const allowedOps = CONTROL_TYPE_OPERATORS[ct] || ['='];
    let op = (init.operator || CONTROL_TYPE_DEFAULT_OP[ct] || allowedOps[0] || '=').toUpperCase();
    if (!allowedOps.includes(op)) op = CONTROL_TYPE_DEFAULT_OP[ct] || allowedOps[0];
    setControlType(ct);
    setValueSourceType(vs.type || 'fixed');
    setOperator(op);
    setPreviewError(null);
    setPreviewValues([]);
    form.setFieldsValue({
      label: init.label || '',
      column: init.column || '',
      control_type: ct,
      operator: op,
      value_source_type: vs.type || 'fixed',
      fixed_values: Array.isArray(vs.values) ? vs.values : [],
      sql: vs.sql || '',
      default_values: Array.isArray(init.default_values) ? init.default_values : [],
    });
    // Notify the agent that the user is editing a filter (creates the
    // `filter_editor` block in the next Data-JSON payload).
    openFilterEditor(init.id ? 'edit' : 'create', init.id ? init : null);
  }, [open, initial, form, openFilterEditor]);

  // Clear the filter-editor focus signal when the modal closes or the
  // component unmounts so the agent doesn't keep seeing a stale draft.
  useEffect(() => {
    if (!open) closeFilterEditor();
    return () => { closeFilterEditor(); };
  }, [open, closeFilterEditor]);

  // Keep the agent's view of the draft in sync with the form. Ant
  // Design's Form doesn't expose a top-level onChange, so we listen on
  // the form container via onValuesChange below in the JSX.
  const handleValuesChange = useCallback((_changed, allValues) => {
    if (!open) return;
    updateFilterEditorDraft(snapshotDraft(allValues));
  }, [open, updateFilterEditorDraft, snapshotDraft]);

  const testSql = useCallback(async () => {
    const sql = form.getFieldValue('sql');
    if (!sql || !sql.trim()) {
      setPreviewError('Enter a SQL statement first.');
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await executeDashboardSQL(sql, 1000, true);
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      const rows = Array.isArray(payload?.rows) ? payload.rows : [];
      const cols = Array.isArray(payload?.columns) ? payload.columns : [];
      const targetCol = (form.getFieldValue('column') || '').trim();
      const namedIdx = targetCol ? cols.indexOf(targetCol) : -1;
      const colIdx = namedIdx >= 0 ? namedIdx : 0;
      const first = rows.map((r) => {
        if (Array.isArray(r)) return r[colIdx];
        if (r && typeof r === 'object') return r[cols[colIdx]] ?? Object.values(r)[colIdx];
        return r;
      });
      const distinct = Array.from(new Set(first.map((v) => (v == null ? '' : String(v)))));
      setPreviewValues(distinct);
      if (distinct.length === 0) setPreviewError('Query returned no rows.');
    } catch (err) {
      setPreviewError(err?.message || 'SQL execution failed.');
      setPreviewValues([]);
    } finally {
      setPreviewLoading(false);
    }
  }, [form]);

  const handleOk = useCallback(async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (_e) { return; }
    const ct = values.control_type || 'selector';
    const allowedOps = CONTROL_TYPE_OPERATORS[ct] || ['='];
    let op = (values.operator || CONTROL_TYPE_DEFAULT_OP[ct] || allowedOps[0]).toUpperCase();
    if (!allowedOps.includes(op)) op = CONTROL_TYPE_DEFAULT_OP[ct] || allowedOps[0];
    const needsSource = VALUE_SOURCE_CONTROL_TYPES.has(ct);
    const def = {
      label: values.label.trim(),
      column: values.column.trim(),
      operator: op,
      control_type: ct,
      // multi_select is derived strictly from control type — it's only
      // true for the multi_selector type. Coercing here means downstream
      // renderers never have to second-guess the operator/type combo.
      multi_select: ct === 'multi_selector',
      default_values: values.default_values || [],
      value_source: !needsSource
        // Input / date / date_range / switch carry no value list.
        ? { type: 'fixed', values: [] }
        : (values.value_source_type === 'fixed'
            ? { type: 'fixed', values: values.fixed_values || [] }
            : { type: 'sql', sql: (values.sql || '').trim() }),
    };
    if (needsSource) {
      if (def.value_source.type === 'sql' && !def.value_source.sql) {
        message.error('SQL value source requires a non-empty SQL.');
        return;
      }
      if (def.value_source.type === 'fixed'
          && (def.value_source.values || []).length === 0) {
        message.error('Fixed value source needs at least one value.');
        return;
      }
    }
    setSubmitting(true);
    try {
      await onSubmit(def);
      onCancel();
    } catch (err) {
      message.error(err?.message || 'Failed to save filter.');
    } finally {
      setSubmitting(false);
    }
  }, [form, onSubmit, onCancel]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText={isEdit ? 'Save' : 'Create'}
      title="Filter Editor"
      confirmLoading={submitting}
      destroyOnClose
      width={620}
    >
      <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
        <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Label required' }]}>
          <Input placeholder="e.g. Region" />
        </Form.Item>
        <Form.Item
          name="column"
          label={(
            <span>
              Target column{' '}
              <Tooltip title="Column name as it appears in the SQL result (case-insensitive). Pick from the dashboard's known columns or type a new one.">
                <span style={{ color: '#999', cursor: 'help' }}>(?)</span>
              </Tooltip>
            </span>
          )}
          rules={[{ required: true, message: 'Column required' }]}
        >
          <AutoComplete
            options={columnOptions}
            placeholder="e.g. REGION"
            allowClear
            filterOption={(input, option) =>
              String(option?.value || '').toLowerCase().includes(String(input || '').toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item
          name="control_type"
          label="Type"
          tooltip="Pick the input widget the user will see in the filter bar."
          initialValue="selector"
          rules={[{ required: true }]}
        >
          <Select
            onChange={(v) => {
              setControlType(v);
              // Reset operator to the type's default when switching types
              // so the form never carries an operator the new type can't
              // express (e.g. switching from `multi_selector` → `date`
              // must drop IN in favour of `=`).
              const allowed = CONTROL_TYPE_OPERATORS[v] || ['='];
              const next = CONTROL_TYPE_DEFAULT_OP[v] || allowed[0];
              setOperator(next);
              form.setFieldsValue({ operator: next });
              // Reset value source when switching to a type that doesn't use one.
              if (!VALUE_SOURCE_CONTROL_TYPES.has(v)) {
                setValueSourceType('fixed');
                form.setFieldsValue({ value_source_type: 'fixed', fixed_values: [], sql: '' });
              }
            }}
            options={CONTROL_TYPES.map((c) => ({
              value: c.value,
              label: (
                <span><span style={{ marginRight: 8 }}>{c.icon}</span>{c.label}</span>
              ),
            }))}
          />
        </Form.Item>
        {(CONTROL_TYPE_OPERATORS[controlType] || []).length > 1 && (
          <Form.Item
            name="operator"
            label="Operator"
            tooltip="The SQL operator applied when the user picks values."
          >
            <Select
              onChange={(v) => setOperator((v || '=').toUpperCase())}
              options={(CONTROL_TYPE_OPERATORS[controlType] || []).map((o) => ({ value: o, label: o }))}
            />
          </Form.Item>
        )}
        {VALUE_SOURCE_CONTROL_TYPES.has(controlType) && (
          <Form.Item label="Value source">
            <Form.Item name="value_source_type" noStyle>
              <Radio.Group
                onChange={(e) => setValueSourceType(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                options={[
                  { value: 'fixed', label: 'Fixed list' },
                  { value: 'sql', label: 'Generated from SQL' },
                ]}
              />
            </Form.Item>
          </Form.Item>
        )}

        {VALUE_SOURCE_CONTROL_TYPES.has(controlType) && valueSourceType === 'fixed' && (
          <Form.Item
            name="fixed_values"
            label="Allowed values"
            tooltip="Type and press Enter"
          >
            <Select
              mode="tags"
              tokenSeparators={[',']}
              placeholder="value1, value2, ..."
            />
          </Form.Item>
        )}

        {VALUE_SOURCE_CONTROL_TYPES.has(controlType) && valueSourceType === 'sql' && (
          <>
            <Form.Item
              name="sql"
              label="SQL (single column SELECT — distinct values used)"
              tooltip="The data-service executes this with limit 1000 to preview the value list."
            >
              <Input.TextArea
                rows={4}
                placeholder="SELECT DISTINCT region FROM analytics.dim_geo ORDER BY 1"
              />
            </Form.Item>
            <Space style={{ marginBottom: 12 }}>
              <Button icon={<ExperimentOutlined />} onClick={testSql} loading={previewLoading}>
                Test SQL
              </Button>
              {previewValues.length > 0 && (
                <span style={{ color: '#666' }}>
                  {previewValues.length} distinct value{previewValues.length === 1 ? '' : 's'}
                </span>
              )}
            </Space>
            {previewError && (
              <Alert type="warning" showIcon message={previewError} style={{ marginBottom: 12 }} />
            )}
            {previewValues.length > 0 && (
              <div style={{ marginBottom: 12, maxHeight: 120, overflow: 'auto' }}>
                {previewValues.slice(0, 50).map((v) => (
                  <Tag key={v} style={{ marginBottom: 4 }}>{v || '(empty)'}</Tag>
                ))}
                {previewValues.length > 50 && (
                  <span style={{ color: '#999' }}>+{previewValues.length - 50} more</span>
                )}
              </div>
            )}
          </>
        )}

        {controlType !== 'switch' && (
          <Form.Item name="default_values" label="Default selected values (optional)">
            <Select mode="tags" tokenSeparators={[',']} placeholder="Pre-selected on load" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
